import React from 'react';
import { Box, BoxProps, Button, Heading, HeadingProps } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import AddressSearch from './AddressSearch';
import { TrackingTaxonomy } from '../globals';
import { CTAProps } from '@opendoor/bricks/complex/CtaInput';

export const SmallContainer: React.FC<BoxProps> = (props) => (
  <Box
    mx="auto"
    maxWidth="720px"
    position="relative"
    _after={{ content: '""', display: 'block', clear: 'both' }}
    sx={{
      '@media (max-width: 767px)': {
        maxWidth: '90%',
      },
    }}
    {...props}
  />
);

export const Disclaimer: React.FC<Omit<HeadingProps, 'size' | 'as'>> = (props) => (
  <Heading
    size="h5"
    color="neutrals70"
    textAlign="center"
    mt="15px"
    fontSize="s0"
    display="none"
    sx={{
      '@media (max-width: 767px)': {
        display: 'block',
      },
    }}
    {...props}
  />
);

export const Bullet = () => (
  <Box as="span" px="5px">
    {String.fromCharCode(0x2022)}
  </Box>
);

interface IProps {
  visible: boolean;
  trackingTaxonomy: TrackingTaxonomy;
  hideDisclaimer?: boolean;
  buttonText?: string;
  placeholderText?: string;
  actionText?: CTAProps['actionText'];
}

const StickyCTABar = ({
  visible,
  trackingTaxonomy,
  hideDisclaimer,
  buttonText,
  actionText,
  placeholderText,
}: IProps) => {
  const [revealHidden, setRevealHidden] = React.useState(false);
  const revealStyle = revealHidden
    ? { display: 'none' }
    : { position: 'absolute' as const, top: 0, left: 0, zIndex: 3, height: '50px' };
  const containerEl = React.useRef<HTMLDivElement | null>(null);
  const { isDesktop } = useDevice();

  return (
    <Box
      className="sticky-cta-bar"
      ref={containerEl}
      position="fixed"
      zIndex={9}
      top={0}
      left="50%"
      width="600px"
      padding="5px"
      opacity={visible ? 1 : 0}
      visibility={visible ? 'visible' : 'hidden'}
      transform="translateX(-50%)"
      transition=" all ease-in-out 0.3s"
      boxShadow="0px 3px 40px rgba(68, 89, 109, 0.1)"
      sx={{
        '-webkit-appearance': 'none',
        '@media (max-width: 978px)': {
          width: '50%',
        },
        '@media (max-width: 767px)': {
          position: 'fixed',
          zIndex: 9,
          // if the revealButton is still there
          // if the revealButton is gone, but it isn't focused yet
          // then keep it at the bottom of the screen
          top: !revealHidden ? 'inherit' : '42px',
          bottom: !revealHidden ? 0 : 'initial',
          width: '100%',
          margin: 0,
          padding: '15px',
          background: 'neutrals0',
        },
        '& label': {
          display: 'none',
        },
      }}
    >
      <SmallContainer>
        {!isDesktop && (
          <Button
            size="md"
            aria-label=""
            style={revealStyle}
            onClick={() => setRevealHidden(!revealHidden)}
            analyticsName="cosmos-sticky-cta-bar-get-free-offer"
          >
            {buttonText || 'Get my free offer'}
          </Button>
        )}
        {visible && (
          <AddressSearch
            trackingTaxonomy={trackingTaxonomy}
            inputLocation="sticky-cta"
            actionText={actionText ?? 'Get my free offer'}
            placeholderText={placeholderText}
            size="sm"
            displayResultsAbove={false}
          />
        )}
        {!hideDisclaimer && (
          <Disclaimer>
            FREE <Bullet /> NO OBLIGATION
          </Disclaimer>
        )}
      </SmallContainer>
    </Box>
  );
};

export default StickyCTABar;
